import { StyleSheet, View, ScrollView } from 'react-native';
import { useEffect, useState } from 'react';
import { useTheme, } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from "react-hook-form";
import { showMessage } from 'react-native-flash-message';
import { createCommonStyles } from '../../styles/commonStyles';
import { CONTROL_FORM_FIELDS } from '../../constants/ControlFormFields';
import CustomLabel from '../../components/CustomLabel/CustomLabel';
import CustomButton from '../../components/CustomButton';
import CustomControlledInput from '../../components/ControlledComponents/CustomControlledInput';
import CustomControlledTimePicker from '../../components/CustomControlledTimePicker/CustomControlledTimePicker';
import CustomControlledCheckbox from '../../components/ControlledComponents/CustomControlledCheckbox';
import CustomControlledColorDots from '../../components/ControlledComponents/CustomControlledColorDots';
import { useShifts } from '../../hooks/UseShifts';
import { useAccountContext } from '../../hooks/useAccountContext';

const ShiftsCreateTemplateScreen = ({ onClose, item }) => {
    const { colors } = useTheme();
    const styles = makeStyles(colors);
    const { account } = useAccountContext();

    const common = createCommonStyles(colors);
    const { saveShiftTemplate, updateShiftTemplate, getShiftTemplateBreaks } = useShifts();
    const [breaksLoaded, setBreaksLoaded] = useState(!item);
    const { t } = useTranslation();
    const {
        TEMPLATENAME,
        SHIFTTYPE,
        STARTTIME,
        ENDTIME,
        DURATIONMINUTES,
        COLORCODE,
        ISOVERNIGHT,
        ISACTIVE,
        BREAKS,
        BREAKSTART,
        BREAKEND,
        BREAKDURATION,
        ISPAID,
    } = CONTROL_FORM_FIELDS.CREATESHIFTS;
    const {
        control,
        handleSubmit,
        watch,
        getValues,
        formState: { errors },
        resetField,
        reset
    } = useForm({
        defaultValues: {
            [TEMPLATENAME]: "",
            [ISACTIVE]: true,
            [BREAKS]: [],
        },
        mode: "onBlur",
        reValidateMode: "onChange",
    });
    const { fields, append, remove } = useFieldArray({ control, name: BREAKS });
    const emptyBreak = { breakStart: '', breakEnd: '', isPaid: true };

    // Time fields are Date objects; convert "HH:mm[:ss]" strings from the API accordingly.
    const toDate = (hhmm) => {
        if (!hhmm) return undefined;
        if (hhmm instanceof Date) return hhmm;
        const [hours, minutes] = String(hhmm).split(':').map(Number);
        if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return undefined;
        const d = new Date();
        d.setHours(hours, minutes, 0, 0);
        return d;
    };

    useEffect(() => {
        if (!item) return;
        let active = true;
        setBreaksLoaded(false);
        getShiftTemplateBreaks(item.id).then((breaks) => {
            if (!active) return;
            reset({
                [TEMPLATENAME]: item.tempName ?? "",
                [SHIFTTYPE]: item.shiftType ?? "",
                [STARTTIME]: toDate(item.startTime),
                [ENDTIME]: toDate(item.endTime),
                [COLORCODE]: item.colorCode ?? "",
                [ISOVERNIGHT]: item.isOvernight ?? false,
                [ISACTIVE]: item.isActive ?? true,
                [BREAKS]: breaks.map((shiftBreak) => ({
                    breakId: shiftBreak.id,
                    [BREAKSTART]: toDate(shiftBreak.breakStart),
                    [BREAKEND]: toDate(shiftBreak.breakEnd),
                    [ISPAID]: shiftBreak.isPaid ?? false,
                })),
            });
            setBreaksLoaded(true);
        }).catch(() => {
            if (active) showMessage({ message: t('common:saveError'), type: 'error', duration: 3000 });
        });
        return () => { active = false; };
    }, [item, reset]);

    //#region Helpers 
    const getDuration = (start, end, isPreview = false) => {
        if (!start || !end) return isPreview ? "" : "00h00m";

        let diffMs = end - start;

        if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;

        const totalMinutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours}h ${minutes}m`;
    }

    const BreakRow = ({ control, index, onRemove }) => {
        return (
            <View style={[styles.row]}>
                <View>
                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:breakStart')} fontSize={14} />
                    <CustomControlledTimePicker
                        name={`${BREAKS}.${index}.${BREAKSTART}`}
                        control={control}
                        rules={{
                            required: t('common:breakStartReq'),
                            validate: (value) => validateBreakStart(value, index),

                        }}
                    />
                </View>
                <View>
                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:breakEnd')} fontSize={14} />
                    <CustomControlledTimePicker
                        name={`${BREAKS}.${index}.${BREAKEND}`}
                        control={control}
                        rules={{
                            required: t('common:breakEndReq'),
                            validate: (value) => validateBreakEnd(value, index),
                        }}
                    />
                </View>
                <View>
                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:duration')} fontSize={14} />
                    <CustomLabel type='MINORTITLE' value={getDuration(watch(`${BREAKS}.${index}.${BREAKSTART}`), watch(`${BREAKS}.${index}.${BREAKEND}`))} fontSize={17} />
                </View>
                <View>
                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:isPaid')} fontSize={14} />
                    <CustomControlledCheckbox
                        control={control}
                        name={`${BREAKS}.${index}.${ISPAID}`}
                    />
                </View>
                <CustomButton
                    onPress={() => onRemove(index)}
                    iconName="trash-outline"
                    show="HOME_BELL"
                    type="HOME_BELL"
                    textTop="0"
                    sz={25}
                />
            </View>
        )
    }

    const hhmm = (v) => {
        const d = new Date(v);
        return isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5);
    };
    const toHHmm = (v) => {
        if (!v) return '';
        if (v instanceof Date) return v.toTimeString().slice(0, 5);
        return String(v).slice(0, 5);
    };
    const toMins = (value) => {
        if (!value) return null;

        if (value instanceof Date) {
            return value.getHours() * 60 + value.getMinutes();
        }

        const [hours, minutes] = String(value).split(':').map(Number);

        if (
            !Number.isInteger(hours) ||
            !Number.isInteger(minutes) ||
            hours < 0 ||
            hours > 23 ||
            minutes < 0 ||
            minutes > 59
        ) {
            return null;
        }

        return hours * 60 + minutes;
    };

    const validateShiftEnd = (value) => {
        const start = toMins(getValues(STARTTIME));
        const end = toMins(value);
        if (start == null || end == null) return true;

        return end > start || t('common:endTimeAfterStart');
    };

    const validateBreakStart = (value, index) => {
        const start = toMins(value);
        const end = toMins(getValues(`${BREAKS}.${index}.${BREAKEND}`));
        const shiftStart = toMins(getValues(STARTTIME));
        const shiftEnd = toMins(getValues(ENDTIME));

        if (start == null) return true;

        if (end != null && start >= end) {
            return t('common:breakStartBeforeEnd');
        }

        if (
            shiftStart != null &&
            shiftEnd != null &&
            (start < shiftStart || start >= shiftEnd)
        ) {
            return t('common:breakWithinShift');
        }

        return true;
    };

    const validateBreakEnd = (value, index) => {
        const start = toMins(getValues(`${BREAKS}.${index}.${BREAKSTART}`));
        const end = toMins(value);
        const shiftStart = toMins(getValues(STARTTIME));
        const shiftEnd = toMins(getValues(ENDTIME));

        if (end == null) return true;

        if (start != null && end <= start) {
            return t('common:breakEndAfterStart');
        }

        if (
            shiftStart != null &&
            shiftEnd != null &&
            (end <= shiftStart || end > shiftEnd)
        ) {
            return t('common:breakWithinShift');
        }

        return true;
    };

    const span = (start, end) => {
        const startMinutes = toMins(start);
        const endMinutes = toMins(end);

        if (startMinutes == null || endMinutes == null) return 0;

        return endMinutes - startMinutes;
    };
    //#endregion

    //#region Save / Errpr
    const onErrorsFound = () => {

    }

    const onSubmit = (form) => {
        console.log(form)
        const start = toHHmm(form[STARTTIME]);

        const end = toHHmm(form[ENDTIME]);
        const isOvernight = toMins(form[ENDTIME]) <= toMins(form[STARTTIME]);
        let payload = {
            Id: item?.id ?? '',
            TempName: form[TEMPLATENAME]?.trim(),
            ShiftType: form[SHIFTTYPE] ?? '',
            StartTime: start,
            EndTime: end,
            DurationMinutes: span(form[STARTTIME], form[ENDTIME]),
            ColorCode: form[COLORCODE] ?? '',
            IsOvernight: isOvernight,
            IsActive: form[ISACTIVE] ?? true,
            CreatedByEmployeeId: account.employeeId,
            // CreatedAt omitted -> let the server set GETUTCDATE()
            Breaks: (form[BREAKS] ?? [])
                .filter((b) => b[BREAKSTART] && b[BREAKEND])
                .map((b) => ({
                    Id: b.breakId ?? '',
                    ShiftTemplateId: item?.id ?? '',
                    BreakStart: toHHmm(b[BREAKSTART]),
                    BreakEnd: toHHmm(b[BREAKEND]),
                    DurationMinutes: span(b[BREAKSTART], b[BREAKEND]),
                    IsPaid: b[ISPAID] ?? false,
                })),
        };

        const request = item
            ? updateShiftTemplate(item.id, payload)
            : saveShiftTemplate(payload);

        request.then((success) => {
            if (success) {
                showMessage({
                    message: t('common:saveSuccess'),
                    type: "success",
                    duration: 3000,
                })
                onClose?.(true);
            }
            else {
                showMessage({
                    message: t('common:saveError'),
                    type: "error",
                    duration: 3000,
                })
            }
        })
    }
    //#endregion
    return (
        <View style={common.container}>
            <View style={common.titleButtonRow}>
                <CustomLabel
                    value={t('navigate:createTemplate')}
                    type='MINORTITLE'
                    fontSize={20}
                />
                <View style={styles.row}>
                    <CustomButton
                        onPress={() => onClose?.()}
                        text={t('common:cancel')}
                        iconName='close-outline'
                        show='BUTTON_CANCEL'
                        type='BUTTON_CANCEL'
                        isGradient={false}
                        textTop={0}
                        sz={20}
                    />
                    {/* //* On Save button for all advanced settings - Promise queuse all axios post changes. */}
                    <CustomButton
                        onPress={handleSubmit(onSubmit, onErrorsFound)}
                        disabled={!breaksLoaded}
                        text={t('common:save')}
                        iconName='save-outline'
                        show='BUTTON_SAVE'
                        type='BUTTON_SAVE'
                        isGradient={true}
                        textTop={0}
                        sz={20}
                    />
                </View>
            </View>

            <View style={common.flexRow}>
                <View style={[styles.rowBasic]}>
                    <View style={common.container}>
                        {/* Basic Details */}
                        <View style={[styles.flexCardPrimary, common.cardstyle]}>
                            <View style={styles.rowTitle}>
                                <CustomLabel
                                    value={t('common:basicDetails')}
                                    type='CARDTITLE_SECONDARY'
                                    iconName="time-outline"
                                    sz={18}
                                    fontSize={16}
                                />
                            </View>
                            <View style={common.divider} />
                            <View style={[styles.row]}>
                                <View>
                                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:tempName')} fontSize={14} />
                                    <CustomControlledInput
                                        name={TEMPLATENAME}
                                        control={control}
                                        type={'MAININPUT'}
                                        rules={{
                                            required: t('common:tempNameReq'),
                                        }}
                                    />
                                </View>
                                <View>
                                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:shiftType')} fontSize={14} />
                                    <CustomControlledInput
                                        name={SHIFTTYPE}
                                        control={control}
                                        type={'MAININPUT'}
                                        maxLen={9}
                                        rules={{
                                            required: t('common:shiftTypeReq'),
                                        }}
                                    />
                                </View>
                            </View>
                            <View style={[styles.row]}>
                                <View>
                                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:startTime')} fontSize={14} />
                                    <CustomControlledTimePicker
                                        name={STARTTIME}
                                        control={control}
                                        rules={{
                                            required: t('common:startTimeReq'),
                                        }}
                                    />
                                </View>
                                <View>
                                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:endTime')} fontSize={14} />
                                    <CustomControlledTimePicker
                                        name={ENDTIME}
                                        control={control}
                                        rules={{
                                            required: t('common:endTimeReq'),
                                            validate: validateShiftEnd,
                                        }}
                                    />
                                </View>
                                <View>
                                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:duration')} fontSize={14} />
                                    <CustomLabel type='MINORTITLE' value={getDuration(watch(STARTTIME), watch(ENDTIME))} fontSize={17} />
                                </View>
                            </View>
                            <View>
                                <CustomLabel type='MINORTITLE_MUTED' value={t('common:colorCode')} fontSize={14} />
                                <View style={styles.colorMargin}>
                                    <CustomControlledColorDots
                                        control={control}
                                        name={COLORCODE}
                                        rules={{ required: t('common:colorCodeReq') }}
                                    />
                                </View>
                            </View>
                            <View style={[styles.row]}>
                                <View>
                                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:isActive')} fontSize={14} />
                                    <CustomControlledCheckbox
                                        control={control}
                                        name={ISACTIVE}
                                    />
                                </View>
                                <View>
                                    <CustomLabel type='MINORTITLE_MUTED' value={t('common:isOvernight')} fontSize={14} />
                                    <CustomControlledCheckbox
                                        control={control}
                                        name={ISOVERNIGHT}
                                    />
                                </View>
                            </View>
                        </View>
                        {/* Break */}
                        <View style={[styles.flexCardSecondary, common.cardstyle]}>

                            <View style={styles.rowTitle}>
                                <CustomLabel
                                    value={t('common:breakWindows')}
                                    type='CARDTITLE_SECONDARY'
                                    iconName="cafe-outline"
                                    sz={18}
                                    fontSize={16}
                                />
                                <CustomButton
                                    onPress={() => append(emptyBreak)}
                                    iconName="add-outline"
                                    show="HOME_BELL"
                                    type="HOME_BELL"
                                    textTop="0"
                                    sz={30}
                                />
                            </View>
                            <View style={common.divider} />
                            <ScrollView>
                                {fields.map((field, index) => (
                                    <BreakRow
                                        key={field.id}
                                        control={control}
                                        index={index}
                                        onRemove={() => remove(index)}
                                    />
                                )
                                )}
                            </ScrollView>
                        </View>
                    </View>
                </View>
                {/* LivePreview */}
                <View style={[common.container, common.cardstyle]}>
                    <CustomLabel
                        value={t('common:livePreview')}
                        type='CARDTITLE_SECONDARY'
                        iconName="eye-outline"
                        sz={18}
                        fontSize={16}
                    />
                    <View style={common.divider} />
                    {watch(TEMPLATENAME) !== '' &&
                        <View>
                            <View style={styles.rowTitle}>
                                <View style={styles.previewRow}>
                                    <View style={[styles.previewDot, { backgroundColor: watch(COLORCODE) || '#ccc' }]} />
                                    <View style={common.container}>
                                        <CustomLabel type='MINORTITLE' value={watch(TEMPLATENAME) ?? ''} fontSize={17} />
                                        <CustomLabel
                                            type='MINORTITLE_MUTED'
                                            value={(() => {
                                                const s = hhmm(watch(STARTTIME));
                                                const e = hhmm(watch(ENDTIME));
                                                return s && e ? `${s} - ${e}` : s || e;
                                            })()}
                                            fontSize={14}
                                        />
                                    </View>
                                </View>
                                <View>
                                    <CustomLabel type='MINORTITLE' value={getDuration(watch(STARTTIME), watch(ENDTIME), true)} fontSize={17} />
                                    <CustomLabel type='MINORTITLE_MUTED' value={watch(ISOVERNIGHT) ? t('common:overnight') : t('common:sameDay')} fontSize={14} />
                                </View>
                            </View>
                            <View style={common.divider} />
                            {watch(BREAKS).filter((b) => b[BREAKSTART] && b[BREAKEND]).length === 0 ? (
                                <CustomLabel type='MINORTITLE_MUTED' value={t('common:noBreaks')} fontSize={16} />
                            ) : (
                                watch(BREAKS)
                                    .filter((b) => b[BREAKSTART] && b[BREAKEND])
                                    .map((b, i) => (
                                        <View
                                            key={i}
                                            style={styles.rowFull}>
                                            <View
                                                style={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: 3,
                                                    backgroundColor: b[ISPAID] ? colors.green : colors.text_muted,
                                                }}
                                            />
                                            <CustomLabel
                                                type='MINORTITLE_MUTED'
                                                value={`${hhmm(b[BREAKSTART])} - ${hhmm(b[BREAKEND])}`}
                                                fontSize={16}
                                            />
                                        </View>
                                    ))
                            )}
                        </View>
                    }
                </View>
            </View>
        </View >
    )
}

const makeStyles = (colors) => StyleSheet.create({
    rowBasic: {
        flexDirection: 'row',
        marginHorizontal: 5,
        flex: 2
    },
    rowFull: {
        flexDirection: 'row',
        marginHorizontal: 5,
        flex: 1
    },
    row: {
        flexDirection: 'row',
        marginVertical: 5,
    },
    colorMargin: {
        margin: 10,
    },
    flexCardPrimary: {
        flex: 2,
    },
    flexCardSecondary: {
        flex: 1,
    },
    rowTitle: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginHorizontal: 10,
    },
    previewRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    previewDot: {
        width: 12,
        height: 12,
        borderRadius: 6,   // half of width -> circle
    },
})

export default ShiftsCreateTemplateScreen; 